import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GlobalSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    require_email_verification: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/settings`);
      setSettings(response.data);
    } catch (error) {
      // If settings don't exist yet, use defaults
      if (error.response?.status === 404) {
        setSettings({ require_email_verification: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/settings`, settings);
      alert('Settings saved successfully!');
      onClose();
    } catch (error) {
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Global Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-600">Loading settings...</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Email Verification Setting */}
            <div className="border-b border-gray-200 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">User Registration</h3>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="require_email_verification"
                      type="checkbox"
                      checked={settings.require_email_verification}
                      onChange={(e) => handleSettingChange('require_email_verification', e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="require_email_verification" className="text-sm font-medium text-gray-700">
                      Require email verification for new users
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      {settings.require_email_verification
                        ? "New users must verify their email before their account becomes active."
                        : "New users are automatically active but still receive verification emails."
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-md">
                <div className="flex">
                  <div className="text-blue-600 mr-3">💡</div>
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How this works:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><strong>Checked:</strong> Users must click the verification link to activate their account</li>
                      <li><strong>Unchecked:</strong> Users are active immediately but still get verification emails</li>
                      <li>Verification links always work regardless of this setting</li>
                      <li>This only affects new registrations going forward</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Future settings can be added here */}
            <div className="text-sm text-gray-500 text-center py-2">
              More global settings will appear here as they are added to the system.
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettings;